<template>
  <div class="card">
    <div class="card__head">
      <div>
        <h2 class="card__title">{{ t("review.title", { version: version.version }) }}</h2>
        <p class="card__sub">{{ t("review.lede") }}</p>
      </div>
      <div class="row">
        <span v-if="review" :class="['badge', statusBadge]">{{ statusLabel }}</span>
        <button type="button" class="btn btn--secondary btn--sm" :disabled="loading" @click="load">{{ t("common.refresh") }}</button>
      </div>
    </div>

    <div v-if="error" class="notice notice--danger"><div class="notice__body">{{ error }}</div></div>
    <div v-else-if="loading && !review" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>
    <p v-else-if="!review" class="muted small">{{ t("review.none") }}</p>

    <template v-else>
      <p class="small muted">
        <template v-if="running">{{ t("review.running") }}</template>
        <template v-else-if="review.ran_at">{{ t("review.ranAt", { when: formatDate(review.ran_at) }) }}</template>
      </p>
      <div class="row row--wrap review__summary">
        <span class="badge badge--success">{{ t("review.passedCount", { count: counts.passed }) }}</span>
        <span :class="['badge', counts.blocking ? 'badge--danger' : '']">{{ t("review.blockingCount", { count: counts.blocking }) }}</span>
        <span :class="['badge', counts.warnings ? 'badge--warn' : '']">{{ t("review.warningCount", { count: counts.warnings }) }}</span>
      </div>

      <ul class="review__checks">
        <li v-for="check in checks" :key="check.name" :class="['review__check', checkClass(check)]">
          <div class="row row--between">
            <div class="row">
              <span :class="['badge', check.pass ? 'badge--success' : check.blocking ? 'badge--danger' : 'badge--warn']">
                {{ check.pass ? t("review.pass") : t("review.fail") }}
              </span>
              <span class="strong small">{{ checkLabel(check.name) }}</span>
              <code class="xs muted">{{ check.name }}</code>
            </div>
            <span class="xs muted">{{ check.blocking ? t("review.blocking") : t("review.warning") }}</span>
          </div>
          <details v-if="hasEvidence(check)" :open="!check.pass" class="review__evidence">
            <summary class="xs">{{ t("review.evidence") }}</summary>
            <pre class="code-block">{{ evidenceText(check.evidence) }}</pre>
          </details>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText, formatDate } from "../../lib/errors";

const props = defineProps({
  appId: { type: String, required: true },
  version: { type: Object, required: true }
});
const emit = defineEmits(["settled"]);
const { t, te } = useI18n();
const errorText = useErrorText();

const RUNNING = ["pending", "queued", "running", "checking", "submitted"];
const POLL_MS = 5000;
const MAX_POLLS = 60;

const review = ref(null);
const loading = ref(false);
const error = ref("");
let timer = null;
let polls = 0;

const checks = computed(() => (Array.isArray(review.value?.checks) ? review.value.checks : []));
const running = computed(() => RUNNING.includes(review.value?.status));
const counts = computed(() => ({
  passed: checks.value.filter((c) => c.pass).length,
  blocking: checks.value.filter((c) => !c.pass && c.blocking).length,
  warnings: checks.value.filter((c) => !c.pass && !c.blocking).length
}));
const statusLabel = computed(() => {
  const s = review.value?.status;
  return te(`review.status.${s}`) ? t(`review.status.${s}`) : s;
});
const statusBadge = computed(() => {
  const s = review.value?.status;
  if (running.value) return "badge--info";
  return s === "passed" ? "badge--success" : s === "failed" ? "badge--danger" : "";
});

function checkLabel(name) {
  return te(`review.checks.${name}`) ? t(`review.checks.${name}`) : name;
}
function checkClass(check) {
  return check.pass ? "" : check.blocking ? "review__check--fail" : "review__check--warn";
}
function hasEvidence(check) {
  const e = check.evidence;
  return e !== undefined && e !== null && e !== "" && !(typeof e === "object" && !Object.keys(e).length);
}
function evidenceText(e) {
  if (typeof e === "string") return e;
  try {
    return JSON.stringify(e, null, 2);
  } catch (err) {
    return String(e);
  }
}

function stop() {
  clearTimeout(timer);
  timer = null;
}

async function load() {
  stop();
  const wasRunning = running.value;
  loading.value = true;
  error.value = "";
  try {
    review.value = (await api.review(props.appId, props.version.id)) || null;
  } catch (e) {
    if (e.status === 404) review.value = null;
    else error.value = errorText(e);
  } finally {
    loading.value = false;
  }
  if (running.value && polls < MAX_POLLS) {
    polls += 1;
    timer = setTimeout(load, POLL_MS);
  } else if (wasRunning && !running.value) {
    emit("settled");
  }
}

watch(
  () => props.version.id,
  () => {
    polls = 0;
    review.value = null;
    load();
  },
  { immediate: true }
);
onBeforeUnmount(stop);
</script>

<style scoped>
.review__summary { margin-bottom: var(--space-3); }
.review__checks { list-style: none; margin: 0; padding: 0; }
.review__check { padding: var(--space-3) 0 var(--space-3) var(--space-3); border-bottom: 1px solid var(--line); border-left: 3px solid var(--green-500); }
.review__check:last-child { border-bottom: 0; }
.review__check--fail { border-left-color: var(--red-500); }
.review__check--warn { border-left-color: var(--amber-500); }
.review__evidence { margin-top: var(--space-2); }
.review__evidence summary { cursor: pointer; color: var(--ink-muted); }
.review__evidence pre { margin-top: var(--space-2); }
</style>
